import { PlusIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/catalyst/badge";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import {
  SupplierInsuranceOptionListItem,
  supplierInsuranceOptionListQuery,
} from "@/app/Suppliers/GraphQL/supplierInsuranceOptionListQuery";
import { useSupplierInsuranceOptionActions } from "@/app/Suppliers/Hooks/useSupplierInsuranceOptionActions";
import { SupplierInsuranceOptionCreateDialog } from "@/app/Suppliers/Components/SupplierInsuranceOptionCreateDialog";
import { SupplierInsuranceOptionEditDialog } from "@/app/Suppliers/Components/SupplierInsuranceOptionEditDialog";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";

export function SupplierInsuranceOptionGrid({
  supplier,
  addLabel = "Add package",
}: {
  supplier: SupplierRecord;
  addLabel?: string;
}) {
  const t = useTranslations("supplier.insurance");
  const getActions = useSupplierInsuranceOptionActions();
  const { open: openCreate } = useDialog(SupplierInsuranceOptionCreateDialog);
  const { open: openEdit } = useDialog(SupplierInsuranceOptionEditDialog);

  const { data } = useGqlQuery(supplierInsuranceOptionListQuery, {
    first: 100,
    supplierId: supplier.id,
  });

  const options = data?.supplierInsuranceOptions.data ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {options.map((option) => (
        <PackageCard
          key={option.id}
          option={option}
          currency={supplier.currency}
          primaryLabel={t("primary")}
          bondLabel={t("bond")}
          liabilityLabel={t("standardLiability")}
          actions={<IconMenuActions actions={getActions(option)} />}
          onEdit={() => openEdit({ insuranceOptionId: option.id })}
        />
      ))}

      <button
        type="button"
        onClick={() =>
          openCreate({
            initialValues: { supplier: { connect: supplier.id } },
          })
        }
        className="flex min-h-[132px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-4 text-sm font-semibold text-gray-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-600 focus:outline-none focus-visible:border-indigo-400"
      >
        <PlusIcon className="size-6" />
        {addLabel}
      </button>
    </div>
  );
}

function PackageCard({
  option,
  currency,
  primaryLabel,
  bondLabel,
  liabilityLabel,
  actions,
  onEdit,
}: {
  option: SupplierInsuranceOptionListItem;
  currency: SupplierRecord["currency"];
  primaryLabel: string;
  bondLabel: string;
  liabilityLabel: string;
  actions: React.ReactNode;
  onEdit: () => void;
}) {
  return (
    <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white transition hover:border-gray-300 hover:shadow-sm">
      <div className="absolute right-2 top-2">{actions}</div>

      <button
        type="button"
        onClick={onEdit}
        className="flex flex-1 flex-col p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
      >
        <div className="flex items-center gap-2 pr-6">
          <span className="truncate font-semibold text-gray-900">
            {option.name}
          </span>
          {option.is_default ? <Badge>{primaryLabel}</Badge> : null}
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-gray-500">{bondLabel}</dt>
            <dd className="mt-0.5 font-medium text-gray-900">
              {formatCurrency(option.bond_amount, currency)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">{liabilityLabel}</dt>
            <dd className="mt-0.5 font-medium text-gray-900">
              {formatCurrency(option.standard_liability_amount, currency)}
            </dd>
          </div>
        </dl>
      </button>
    </div>
  );
}
